import { Injectable, Injector, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { gigyaInfo } from '../../types/gigyaInfo';
import { MessagePopupComponent } from '../../shared/components/message-popup/message-popup.component';
import { PopupService } from '../../core/../core/services/popup/popup.service';

@Injectable({
	providedIn: 'root'
})

export class LoginService {

	private url = environment.do_services_sb_url;
	public gigyaResponse: gigyaInfo = {} as gigyaInfo;
	gigyaToken: BehaviorSubject<string> = new BehaviorSubject<string>('');
	accessToken: BehaviorSubject<string> = new BehaviorSubject<string>('');

	private readonly STORAGE_KEY = 'gigyaResponse';
	private passwordResetUrl: string = 'gigya/forgotPasswordReset';
	private activateUserUrl: string = 'gigya/resendActivateLink';
	private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

	// Signals replacing BehaviorSubject (hydrate from sessionStorage if present)
	private gigyaResponseSignal = signal<gigyaInfo>(this.restore());

	// Public readonly signals for components to consume
	getGigyaResp = this.gigyaResponseSignal.asReadonly();

	constructor(private http: HttpClient,
				private popupService: PopupService, private route: Router, private injector: Injector) {
					
		// Try hydrate from storage on construction
		const stored = this.restore();
		if(stored) {
			this.gigyaResponse = stored;
			this.gigyaResponseSignal.set(stored);
		}
	}

	private restore(): gigyaInfo {
		try {
			const raw = sessionStorage.getItem(this.STORAGE_KEY);
			return raw ? JSON.parse(raw) as gigyaInfo : ({} as gigyaInfo);
		} catch (e) {
			return {} as gigyaInfo;
		}
	}

	private persist(value: gigyaInfo) {
		try {
			sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(value));
		} catch (e) {
			// swallow
		}
	}

	// Method to update the gigya response signal
	setGigyaResponse(value: gigyaInfo) {
		this.gigyaResponse = value;
		this.gigyaResponseSignal.set(this.gigyaResponse);
		this.persist(this.gigyaResponse);
	}

	clearGigyaResponse() {
		this.gigyaResponse = {} as gigyaInfo;
		this.gigyaResponseSignal.set(this.gigyaResponse);
		sessionStorage.removeItem(this.STORAGE_KEY);
	}

	passwordReset(userData: string): Observable<any> {
		let options = {
			headers: this.headers
		}
		return this.http.post(this.url + this.passwordResetUrl, userData, options).pipe(
			map((response: any) => {
				const run = response;
				return run;
			}),
			catchError((e: any) => this.handleError(e))
		);
	}

	activateUser(userData: string): Observable<any> {
		let options = {
			headers: this.headers
		}
		return this.http.post(this.url + this.activateUserUrl, userData, options).pipe(
			map((response: any) => {
				const run = response;
				return run;
			}),
			catchError((e: any) => this.handleError(e))
		);
	}

	private handleError(error: any): Observable<never> {
        if([400, 403, 404, 405, 500, 502, 503, 504].includes(error.status)) {
            return throwError(() => error);
        }
        if(error.status === 401) {
			const dialogRef = this.popupService.openDialog(MessagePopupComponent, {
				data: { message: 'Unauthorized Request - Your Session has expired' }
			});
			dialogRef.afterClosed().subscribe(async () => {
				const { AuthService } = await import('../../core/services/auth/auth.service');
				const authService = this.injector.get(AuthService);
				authService.logout().subscribe(() => {
					this.route.navigate(['/home']);
				});
			});
			return throwError(() => new Error('Unauthorized Request - Session expired'));
		}
        return throwError(() => new Error(`Unhandled status code: ${error.status}`));
    }
}